<script setup lang="ts">
import { Camera } from '@lucide/vue'
import {
    CHAT_UNPROMPTED_TURN_LIMITS,
    chatUnpromptedTurnLimitChoices,
    chatUnpromptedTurnLimitLabel,
    isChatUnpromptedTurnLimit,
    isLiveRun,
} from '@zerohuman/shared'
import {
    Button,
    Input,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
    ZhFieldError,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
} from '@zerohuman/ui'
import { computed, nextTick, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import {
    api,
    type Blockers,
    type ChatMemoryDepthSetting,
    type ChatModelTierSetting,
    type ChatUnpromptedTurnLimitSetting,
    type ConcurrencySetting,
    type EnterpriseChangelogRepoSetting,
    type EnterpriseTimezoneSetting,
    type EnterpriseWorkingHours,
    formatUsdCents,
    type GateRow,
    isValidConcurrentRunLimit,
    type KpiBoard,
    type ListResponse,
    type Me,
    type RunRow,
    refusalCode,
    type SpendSnapshot,
    type Team,
} from '@/api'
import AgentConnectionsPanel from '@/components/AgentConnectionsPanel.vue'
import EnterpriseAvatar from '@/components/EnterpriseAvatar.vue'
import EnterpriseDangerZone from '@/components/EnterpriseDangerZone.vue'
import EnterpriseKpisPanel from '@/components/EnterpriseKpisPanel.vue'
import EnterpriseSignatureTemplatePanel from '@/components/EnterpriseSignatureTemplatePanel.vue'
import FirstRunHint from '@/components/FirstRunHint.vue'
import HqTile from '@/components/HqTile.vue'
import KpiBoardPanel from '@/components/KpiBoardPanel.vue'
import TaskKpiRows from '@/components/TaskKpiRows.vue'
import ToolBindings from '@/components/ToolBindings.vue'
import { useBlockLoads } from '@/composables/useBlockLoads'
import { useUiMode } from '@/composables/useUiMode'
import { avatarDataUrl } from '@/lib/avatar'
import { blockerAlertTotal } from '@/lib/blockers'
import { chatMemoryChosenLabel, chatMemoryDepthLabel } from '@/lib/chatMemoryDepth'
import { announceSessionRead } from '@/lib/sessionRead'
import { availableTimezones } from '@/lib/timezone'
import { reportHandledError } from '@/sentry'
import { type TaskKpi, taskKpisApi } from '@/task-kpis-api'

const me = ref<Me | null>(null)
const teams = ref<Team[]>([])
/** Each team's task KPIs by team id (#900), shown under that team and no other. */
const teamTaskKpis = ref<Map<string, TaskKpi[]>>(new Map())
/** The teams with task KPIs of their own, in the order HQ lists teams. */
const teamsWithTaskKpis = computed(() => teams.value.filter(team => teamTaskKpis.value.get(team.id)?.length))
/** Task KPIs nobody has signed off yet: proposals waiting on a person to sign them off or withdraw them. */
const awaitingSignOff = computed(() => [...teamTaskKpis.value.values()].flat().filter(kpi => !kpi.signedOff).length)
/**
 * `null` until the waiting-gates fetch resolves (and it stays `null` if that fetch fails), so an
 * empty list in flight never reads as "no waiting gates". The tile and list read through `gates`.
 */
const loadedGates = ref<GateRow[] | null>(null)
const gates = computed(() => loadedGates.value ?? [])
const spend = ref<SpendSnapshot | null>(null)
const blockers = ref<Blockers | null>(null)
const runs = ref<ListResponse<RunRow> | null>(null)
/**
 * Each block loads on its own (#1677). One that fails is named on screen by the label it carries there, reported to
 * Sentry, and cleared by its own next good load, so one dead source leaves the blocks that did load standing.
 */
const { failed: failedBlocks, load } = useBlockLoads()
/** The blocks that could not load, as a sentence reads them: "Spend today", "Spend today, Blockers and Live runs". */
const failedBlockList = computed(() => {
    const names = failedBlocks.value
    return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : (names[0] ?? '')
})
const concurrency = ref<ConcurrencySetting | null>(null)
/** What the field holds: the kit Input hands a number field's value back as a number. */
const concurrencyInput = ref<string | number>('')
const concurrencyError = ref<string | null>(null)
const chatTier = ref<ChatModelTierSetting | null>(null)
const chatTierError = ref<string | null>(null)
const chatMemory = ref<ChatMemoryDepthSetting | null>(null)
const chatMemoryError = ref<string | null>(null)
/**
 * How many turns in a row a member may take on its own in chat (#287): the limit in force as the API reports it, and
 * then as each save answers. `null` until it is read, and when it cannot be: the control is not drawn.
 */
const unpromptedTurns = ref<ChatUnpromptedTurnLimitSetting | null>(null)
const unpromptedTurnsError = ref<string | null>(null)
/** Off, then 1 to 20, as the kit select carries them: the limit said as a string. Read from the shared contract. */
const unpromptedTurnChoices = chatUnpromptedTurnLimitChoices().map(limit => ({
    value: String(limit),
    label: chatUnpromptedTurnLimitLabel(limit),
}))
/**
 * The portal-wide Standard/Advanced mode (#1749). The Chat memory picker words each depth as Light, Balanced or Deep
 * with its cost (#1764); only Advanced adds the exact token cap. The item values stay the raw depth keys.
 */
const { mode: uiMode } = useUiMode()
const enterpriseTimezone = ref<EnterpriseTimezoneSetting | null>(null)
const enterpriseTimezoneError = ref<string | null>(null)
/**
 * The enterprise's name as the Name setting shows it (#2217): read with the session, then the name the rename API
 * answered with, which is the one stored. The field holds what the owner is typing.
 */
const enterpriseName = ref<string | null>(null)
const enterpriseNameInput = ref<string | number>('')
const enterpriseNameError = ref<string | null>(null)
/** What the last save did, said under the heading as the working-hours setting says it: "Saved." and the old name. */
const enterpriseNameNotice = ref<string | null>(null)
const enterpriseNameBusy = ref(false)
/** The repository that hosts the company's public changelog (#1915). `null` until it is read, and when it cannot be. */
const changelogRepo = ref<EnterpriseChangelogRepoSetting | null>(null)
/** What the field holds: the saved repository, or what was typed since. Empty for none, never the word null. */
const changelogRepoInput = ref<string | number>('')
const changelogRepoError = ref<string | null>(null)
/** A save of the changelog repository is in flight: the button says so and a second save waits for it to end. */
const changelogRepoBusy = ref(false)
/** The last save landed. What it says shows only while the field still holds what that save stored. */
const changelogRepoSaved = ref(false)
const changelogRepoSavedNote = computed(() => {
    const stored = changelogRepo.value?.changelogRepo ?? ''
    if (!changelogRepoSaved.value || String(changelogRepoInput.value).trim() !== stored) {
        return null
    }
    return stored ? 'Saved.' : 'Saved. No public changelog is set.'
})
/**
 * Whether quiet source updates apply to the company's linked clones by themselves (#759). `null` until it is read,
 * and when it cannot be; otherwise the position the switch shows.
 */
const autoUpdateClonedTasks = ref<boolean | null>(null)
const autoUpdateClonedTasksError = ref<string | null>(null)
/** A save of the switch is in flight: the switch waits, so a second flip sends nothing until the first lands. */
const autoUpdateClonedTasksBusy = ref(false)
/** The enterprise's saved working hours (#2006); `null` until read, and it stays `null` if that read fails. */
const enterpriseHours = ref<EnterpriseWorkingHours | null>(null)
/** What the start and end fields hold: the kit Input hands its value back as typed. */
const enterpriseHoursStart = ref<string | number>('')
const enterpriseHoursEnd = ref<string | number>('')
const enterpriseHoursError = ref<string | null>(null)
/** What the last Save or Clear did, said once it lands, as the team charter's Save says "Saved.". */
const enterpriseHoursNotice = ref<string | null>(null)
/** A save or clear is in flight: the buttons wait, so a second click sends nothing. */
const savingEnterpriseHours = ref(false)
const avatarInput = ref<HTMLInputElement | null>(null)
const avatarBusy = ref(false)
const avatarError = ref<string | null>(null)
/** `null` until the board is read, and it stays `null` when that read fails: an empty board is not "all green". */
const kpiBoard = ref<KpiBoard | null>(null)
const kpiError = ref<string | null>(null)

/**
 * The kit Input and Textarea hand what was typed up on the next tick, so a form submitted in the same tick as
 * the last keystroke (an autofill that submits straight away) waits for it rather than saving the value before.
 */
function fieldsSettled(): Promise<void> {
    return nextTick()
}

/** The enterprise's picture, changed from the avatar itself; `me` is re-read so the new `?v=` URL shows. */
async function onAvatarPicked(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    input.value = ''
    if (!file || avatarBusy.value) return
    avatarBusy.value = true
    avatarError.value = null
    try {
        await api.setEnterpriseAvatar(await avatarDataUrl(file))
        me.value = await api.me()
    } catch (caught) {
        avatarError.value = `Could not update the picture${caught instanceof Error ? ` (${caught.message})` : ''}.`
    } finally {
        avatarBusy.value = false
    }
}

const blockerCount = computed(() => (blockers.value ? blockerAlertTotal(blockers.value) : null))
/**
 * The API filters to live runs (unfinished, not owned by Blockers), so `total` is the full count even when more
 * live runs exist than one page holds. `null` until loaded, so a failed load never reads as "0 live runs".
 */
const liveRunCount = computed(() => {
    const page = runs.value
    if (!page) {
        return null
    }
    const onPage = page.data.filter(isLiveRun).length
    return page.total > page.data.length ? page.total : onPage
})
/** `formatUsdCents(undefined)` reads "unlimited"; before spend loads (or if it fails) show a placeholder instead. */
const spendToday = computed(() => (spend.value ? formatUsdCents(spend.value.spentTodayCents) : '—'))
/**
 * Nothing waiting, nothing running, nothing blocked — and all three sources actually loaded (each is
 * `null` until then, so a slow or dead source never fakes a first run; the tiles load independently,
 * so without that guard a fast "0 runs" could show this card while gates are still in flight). That is
 * the one state where four zeros say nothing useful, so Home names the next step instead.
 */
const firstRun = computed(
    () =>
        loadedGates.value !== null &&
        loadedGates.value.length === 0 &&
        liveRunCount.value === 0 &&
        blockerCount.value === 0,
)

onMounted(async () => {
    await Promise.all([
        load(
            'Enterprise',
            () => api.me(),
            value => {
                me.value = value
                enterpriseName.value = value?.enterprise.name ?? null
                enterpriseNameInput.value = enterpriseName.value ?? ''
            },
        ),
        load(
            'Teams',
            () => api.teams(),
            value => {
                teams.value = value
            },
        ),
        load(
            'Task KPIs',
            () => taskKpisApi.byTeam(),
            value => {
                teamTaskKpis.value = new Map(value.map(row => [row.teamId, row.kpis]))
            },
        ),
        load(
            'Waiting gates',
            () => api.waitingGates(),
            value => {
                loadedGates.value = value
            },
        ),
        load(
            'Spend today',
            () => api.spend(),
            value => {
                spend.value = value
            },
        ),
        load(
            'Blockers',
            () => api.blockers(),
            value => {
                blockers.value = value
            },
        ),
        load(
            'Live runs',
            () => api.runs({ live: 'true', limit: 100 }),
            value => {
                runs.value = value
            },
        ),
        load(
            'Chat model',
            () => api.chatModelTier(),
            value => {
                chatTier.value = value
            },
        ),
        load(
            'Chat memory',
            () => api.chatMemoryDepth(),
            value => {
                chatMemory.value = value
            },
        ),
        load(
            'Timezone',
            () => api.enterpriseTimezone(),
            value => {
                enterpriseTimezone.value = value
            },
        ),
        load(
            'Concurrent runs',
            () => api.concurrency(),
            value => {
                concurrency.value = value
                concurrencyInput.value = String(value.enterpriseLimit)
            },
        ),
        loadChangelogRepo(),
        loadUnpromptedTurns(),
        loadAutoUpdateClonedTasks(),
        loadKpiBoard(),
        loadEnterpriseHours(),
    ])
})

/**
 * The public changelog repository is read apart from HQ's blocks (#1915). It is a setting most enterprises leave
 * empty, not something HQ reports on, so a read that fails shows no field instead of joining the failure line, the
 * way an API older than the timezone setting shows no timezone control. Runs read the stored value themselves, so
 * nothing they do waits on this screen.
 */
async function loadChangelogRepo(): Promise<void> {
    try {
        showChangelogRepo(await api.enterpriseChangelogRepo())
    } catch {
        changelogRepo.value = null
    }
}

/** The stored repository, and the field set to it: empty when there is none. */
function showChangelogRepo(setting: EnterpriseChangelogRepoSetting): void {
    changelogRepo.value = setting
    changelogRepoInput.value = setting.changelogRepo ?? ''
}

/**
 * Name, change or clear the repository. An emptied field clears it, which means no public changelog. While the save
 * is in flight the button says "Saving…" and another click or Enter does nothing; once it lands a note says it is
 * saved, and that none is set when it was cleared. A refusal renders beside the field and what was typed stays, so
 * it can be corrected rather than typed again.
 */
async function saveChangelogRepo(): Promise<void> {
    if (changelogRepoBusy.value) {
        return
    }
    changelogRepoBusy.value = true
    changelogRepoError.value = null
    changelogRepoSaved.value = false
    try {
        await fieldsSettled()
        const typed = String(changelogRepoInput.value).trim()
        showChangelogRepo(await api.setEnterpriseChangelogRepo(typed === '' ? null : typed))
        changelogRepoSaved.value = true
    } catch (caught) {
        changelogRepoError.value = changelogRepoRefusal(caught)
    } finally {
        changelogRepoBusy.value = false
    }
}

/** The code the API refuses a changelog repository with. Its sentence then starts with the field's name. */
const INVALID_CHANGELOG_REPO = 'invalid_changelog_repo'

/**
 * What a refused save says beside the field. The API names the field first (`changelogRepo: …`) for API callers;
 * Standard drops that name, the way a run's reason drops its leading code, and Advanced keeps the whole sentence. A
 * refusal with another code, or none, shows the server's words as they came; one with no words says it could not
 * save.
 */
function changelogRepoRefusal(caught: unknown): string {
    if (!(caught instanceof Error) || !caught.message) {
        return 'Could not save.'
    }
    if (uiMode.value === 'advanced' || refusalCode(caught) !== INVALID_CHANGELOG_REPO) {
        return caught.message
    }
    return caught.message.replace(/^changelogRepo\s*:\s*/, '') || 'Could not save.'
}

/**
 * The auto-update setting for cloned tasks is read apart from HQ's blocks (#759), as the changelog repository is: a
 * read that fails shows no switch instead of joining the failure line. Clones read the stored value on the server,
 * so nothing they do waits on this screen.
 */
async function loadAutoUpdateClonedTasks(): Promise<void> {
    try {
        autoUpdateClonedTasks.value = (await api.enterpriseAutoUpdateClonedTasks()).autoUpdateClonedTasks
    } catch {
        autoUpdateClonedTasks.value = null
    }
}

/**
 * Saves when flipped, with no Save button, as Spend's switch does. The switch moves at once and then shows what the
 * API stored; a refused save puts it back where it was and says why inside the section. The switch is disabled while
 * a save is in flight, so a second flip cannot race the first.
 */
async function saveAutoUpdateClonedTasks(next: boolean): Promise<void> {
    const previous = autoUpdateClonedTasks.value
    autoUpdateClonedTasksBusy.value = true
    autoUpdateClonedTasksError.value = null
    autoUpdateClonedTasks.value = next
    try {
        autoUpdateClonedTasks.value = (await api.setEnterpriseAutoUpdateClonedTasks(next)).autoUpdateClonedTasks
    } catch (caught) {
        autoUpdateClonedTasks.value = previous
        autoUpdateClonedTasksError.value =
            caught instanceof Error && caught.message ? caught.message : 'Could not save. Try again.'
    } finally {
        autoUpdateClonedTasksBusy.value = false
    }
}

/**
 * The enterprise's working hours (#2006), shown inside the timezone section. Read on its own: a failed read leaves
 * the hours out and the timezone, and every other block, standing.
 */
async function loadEnterpriseHours(): Promise<void> {
    try {
        showEnterpriseHours(await api.enterpriseWorkingHours())
    } catch {
        enterpriseHours.value = null
    }
}

/** The saved hours, into the fields: empty where none are set. */
function showEnterpriseHours(value: EnterpriseWorkingHours): void {
    enterpriseHours.value = value
    enterpriseHoursStart.value = value.start ?? ''
    enterpriseHoursEnd.value = value.end ?? ''
}

/** Saves the typed start and end. A refusal renders verbatim, and the fields go back to what is saved. */
async function saveEnterpriseHours(): Promise<void> {
    if (savingEnterpriseHours.value) {
        return
    }
    enterpriseHoursError.value = null
    enterpriseHoursNotice.value = null
    savingEnterpriseHours.value = true
    await fieldsSettled()
    try {
        showEnterpriseHours(
            await api.setEnterpriseWorkingHours({
                start: String(enterpriseHoursStart.value),
                end: String(enterpriseHoursEnd.value),
            }),
        )
        enterpriseHoursNotice.value = 'Saved.'
    } catch (caught) {
        enterpriseHoursError.value = caught instanceof Error ? caught.message : 'Could not save.'
        if (enterpriseHours.value) {
            showEnterpriseHours(enterpriseHours.value)
        }
    } finally {
        savingEnterpriseHours.value = false
    }
}

/** Clears the enterprise's hours: 8:00am to 8:00pm applies again. */
async function clearEnterpriseHours(): Promise<void> {
    if (savingEnterpriseHours.value) {
        return
    }
    enterpriseHoursError.value = null
    enterpriseHoursNotice.value = null
    savingEnterpriseHours.value = true
    try {
        showEnterpriseHours(await api.clearEnterpriseWorkingHours())
        enterpriseHoursNotice.value = 'Cleared. The default, 8:00am to 8:00pm, applies.'
    } catch (caught) {
        enterpriseHoursError.value = caught instanceof Error ? caught.message : 'Could not clear.'
    } finally {
        savingEnterpriseHours.value = false
    }
}

/**
 * The board loads on its own and says so when it cannot: a dead KPI read must not read as "nothing is
 * RED", and it must not take the rest of HQ's tiles down with it either. Its failure is reported like any
 * other block's (#1677), though it keeps its own message inside the board.
 *
 * It loads again each time the KPIs above it change, so it reports the way the Waiting list does: once each time
 * it stops loading. A board that stays dead across several reloads is one event, not one per reload, and the next
 * failure after a good read is reported again.
 */
async function loadKpiBoard(): Promise<void> {
    try {
        kpiBoard.value = await api.kpiBoard()
        kpiError.value = null
    } catch (caught) {
        const alreadyFailing = kpiError.value !== null
        kpiBoard.value = null
        kpiError.value = 'Could not read the KPI board.'
        if (!alreadyFailing) {
            reportHandledError(caught, 'KPI board')
        }
    }
}

/** How much of the enterprise's memory a chat reply reads. Deeper finds more and costs more tokens per message. */
async function saveChatMemoryDepth(depth: string): Promise<void> {
    chatMemoryError.value = null
    try {
        chatMemory.value = await api.setChatMemoryDepth(depth)
    } catch (caught) {
        chatMemoryError.value = caught instanceof Error ? caught.message : 'Could not save.'
    }
}

/**
 * The unprompted-turn limit is read apart from HQ's blocks (#287), as the changelog repository is: somebody who may
 * not read it, or an API older than the setting, gets no control, and the read joins no failure line. Only a limit
 * the OS could have saved is shown, so the picker never shows a value it does not offer.
 */
async function loadUnpromptedTurns(): Promise<void> {
    try {
        const setting = await api.chatUnpromptedTurnLimit()
        unpromptedTurns.value = isChatUnpromptedTurnLimit(setting?.limit) ? { limit: setting.limit } : null
    } catch {
        unpromptedTurns.value = null
    }
}

/**
 * Save a new limit. The control then shows what the API saved; a refusal shows its message and leaves the limit in
 * force on screen, since nothing changed.
 */
async function saveUnpromptedTurns(chosen: string): Promise<void> {
    unpromptedTurnsError.value = null
    try {
        unpromptedTurns.value = await api.setChatUnpromptedTurnLimit(Number(chosen))
    } catch (caught) {
        unpromptedTurnsError.value = caught instanceof Error ? caught.message : 'Could not save.'
    }
}

/** Which tier a chat turn runs on. Spend caps still tighten it, so raising it cannot outspend the caps. */
async function saveChatTier(tier: string): Promise<void> {
    chatTierError.value = null
    try {
        chatTier.value = await api.setChatModelTier(tier)
    } catch (caught) {
        chatTierError.value = caught instanceof Error ? caught.message : 'Could not save.'
    }
}

/**
 * The zones the CEO can move the company to. Every zone this browser knows, with the one it is in and the
 * current value first so the common choices need no scrolling.
 */
const timezoneChoices = computed(() => {
    const here = Intl.DateTimeFormat().resolvedOptions().timeZone
    return availableTimezones(enterpriseTimezone.value?.timezone, here)
})

/**
 * Move the company to another zone (#884). Set from the creating CEO's browser; changed here afterwards, which
 * moves the next fire of every schedule that follows the enterprise. A refusal renders verbatim.
 */
async function saveEnterpriseTimezone(timezone: string): Promise<void> {
    enterpriseTimezoneError.value = null
    try {
        enterpriseTimezone.value = await api.setEnterpriseTimezone(timezone)
    } catch (caught) {
        enterpriseTimezoneError.value = caught instanceof Error ? caught.message : 'Could not save.'
    }
}

/**
 * Renames the enterprise (#2217). The answer is the name as stored (trimmed), shown at once; the session is then read
 * again and handed to the shell, so the header and the enterprise picker show the new name without a reload. A
 * refusal renders verbatim in the section, and the old name stays. A save already on its way is not sent again, so
 * pressing Enter twice renames once. A save says so, naming the old name, so a mistyped name can be put back.
 */
async function saveEnterpriseName(): Promise<void> {
    if (enterpriseNameBusy.value) {
        return
    }
    enterpriseNameError.value = null
    enterpriseNameNotice.value = null
    enterpriseNameBusy.value = true
    await fieldsSettled()
    try {
        const previous = enterpriseName.value
        const saved = await api.setEnterpriseName(String(enterpriseNameInput.value))
        enterpriseName.value = saved.name
        enterpriseNameInput.value = saved.name
        enterpriseNameNotice.value = previous && previous !== saved.name ? `Saved. It was ${previous}.` : 'Saved.'
    } catch (caught) {
        enterpriseNameError.value = caught instanceof Error ? caught.message : 'Could not save.'
        return
    } finally {
        enterpriseNameBusy.value = false
    }
    await rereadSession()
}

/** The session read again after a change to it, and handed to the shell. A failed read leaves what is shown. */
async function rereadSession(): Promise<void> {
    try {
        const fresh = await api.me()
        if (fresh) {
            me.value = fresh
            announceSessionRead(fresh)
        }
    } catch {
        // The rename is saved and shown here already; the header catches up on the next load.
    }
}

/** Raises or lowers the enterprise-wide concurrent-run limit; a refusal renders verbatim, not a generic failure. */
async function saveConcurrency(): Promise<void> {
    concurrencyError.value = null
    await fieldsSettled()
    const parsed = Number(concurrencyInput.value)
    if (!isValidConcurrentRunLimit(parsed)) {
        concurrencyError.value = 'Enter a whole number of at least 1.'
        return
    }
    try {
        concurrency.value = await api.setConcurrency(parsed)
        concurrencyInput.value = String(concurrency.value.enterpriseLimit)
    } catch (caught) {
        concurrencyError.value = caught instanceof Error ? caught.message : 'Could not save.'
    }
}
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<div class="flex items-center gap-4">
			<div v-if="me" class="relative shrink-0">
				<EnterpriseAvatar :enterprise="me.enterprise" class="size-16" fallback-class="text-xl" />
				<Button
					type="button"
					size="icon-sm"
					variant="outline"
					class="absolute -right-2 -bottom-2"
					aria-label="Change picture"
					:disabled="avatarBusy"
					@click="avatarInput?.click()"
				>
					<Camera />
				</Button>
				<input
					ref="avatarInput"
					type="file"
					accept="image/png,image/jpeg,image/webp,image/gif"
					class="hidden"
					data-testid="enterprise-avatar-file"
					@change="onAvatarPicked"
				/>
			</div>
			<ZhPageTitle kicker="Enterprise">{{ me?.enterprise.name ?? 'Enterprise' }}</ZhPageTitle>
		</div>
		<p v-if="avatarError" class="text-destructive text-sm" role="alert">{{ avatarError }}</p>
		<p class="text-muted-foreground text-sm">
			{{ teams[0]?.name ?? 'Engineering' }}
			<template v-if="teams[0]?.leadName"> · lead {{ teams[0].leadName }}</template>
			<template v-else-if="teams[0]?.cooActingAsLead"> · COO acting as lead</template>
		</p>
		<p v-if="failedBlocks.length" data-testid="home-load-error" class="text-destructive text-sm" role="alert">
			Could not load {{ failedBlockList }}.
		</p>
		<ZhPanel v-if="spend?.enabled !== false && spend?.warning80" as="div" class="text-destructive text-sm">
			Spend is at {{ Math.round(spend.usedRatio * 100) }}% of the tightest day cap
			({{ formatUsdCents(spend.spentTodayCents) }} of {{ formatUsdCents(spend.tightestDayCapCents) }}).
			<RouterLink class="underline" to="/spend">Open Spend</RouterLink>
		</ZhPanel>
		<div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
			<HqTile testid="hq-tile-gates" to="/gates" label="Waiting gates" :value="gates.length" />
			<HqTile testid="hq-tile-blockers" to="/blockers" label="Blockers" :value="blockerCount ?? '—'" />
			<HqTile testid="hq-tile-runs" to="/runs" label="Live runs" :value="liveRunCount ?? '—'" />
			<HqTile testid="hq-tile-spend" to="/spend" label="Spend today" :value="spendToday">
				<p
					v-if="spend?.enabled !== false && spend?.tightestDayCapCents != null"
					class="text-muted-foreground text-xs tabular-nums"
				>
					of {{ formatUsdCents(spend.tightestDayCapCents) }} day cap
				</p>
			</HqTile>
		</div>
		<!--
			The company's own KPIs, and the only place on HQ they are written (#895): KPIs replaced the free-text
			"Company metrics" card. That text is kept, read-only, and was carried over as KPIs waiting for a target.
		-->
		<ZhPanel as="article">
			<EnterpriseKpisPanel @changed="loadKpiBoard" />
		</ZhPanel>
		<!-- Each team's key KPI. The company's own KPIs are in the card above, so the board leaves them out. -->
		<KpiBoardPanel :board="kpiBoard" :error="kpiError" :include-company="false" />
		<!-- #900: each team's task KPIs, under the team whose tasks they measure. Its key KPI is on the board above. -->
		<ZhPanel v-if="teamsWithTaskKpis.length" as="article" data-testid="task-kpis-card">
			<p class="text-muted-foreground text-xs uppercase">Task KPIs</p>
			<p v-if="awaitingSignOff" data-testid="task-kpis-awaiting-sign-off" class="mt-1 text-sm">
				{{ awaitingSignOff }} {{ awaitingSignOff === 1 ? 'KPI is' : 'KPIs are' }} awaiting sign-off. Open
				one to sign it off or withdraw it.
			</p>
			<ul class="mt-2 space-y-2">
				<li
					v-for="team in teamsWithTaskKpis"
					:key="team.id"
					:data-testid="`team-task-kpis-${team.id}`"
					class="text-sm"
				>
					<span class="text-muted-foreground">{{ team.name }}</span>
					<TaskKpiRows class="mt-1 pl-3" :kpis="teamTaskKpis.get(team.id) ?? []" link-tasks />
				</li>
			</ul>
		</ZhPanel>
		<ZhPanel v-if="chatTier" data-testid="chat-model-tier-section" measure="form" class="space-y-2">
			<ZhSectionHeading>Chat model</ZhSectionHeading>
			<p v-if="chatTierError" class="text-destructive text-sm">{{ chatTierError }}</p>
			<Select
				:model-value="chatTier.chatModelTier"
				data-testid="chat-model-tier"
				aria-label="Chat model tier"
				@update:model-value="saveChatTier(String($event))"
			>
				<SelectTrigger class="w-full" aria-label="Chat model tier">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem v-for="tier in chatTier.tiers" :key="tier" :value="tier">{{ tier }}</SelectItem>
				</SelectContent>
			</Select>
			<p class="text-muted-foreground text-xs">
				Which model your members think with in chat. Chat orchestrates — it lists tasks, starts them and
				reports back — and the work itself happens inside a run, which carries the tier of the task that
				defines it. Economical suits that. Spend caps still apply on top of whatever you pick here.
			</p>
		</ZhPanel>
		<ZhPanel v-if="chatMemory" data-testid="chat-memory-depth-section" measure="form" class="space-y-2">
			<ZhSectionHeading>Chat memory</ZhSectionHeading>
			<p v-if="chatMemoryError" class="text-destructive text-sm" role="alert">{{ chatMemoryError }}</p>
			<Select
				:model-value="chatMemory.chatMemoryDepth"
				data-testid="chat-memory-depth"
				aria-label="Chat memory depth"
				@update:model-value="saveChatMemoryDepth(String($event))"
			>
				<SelectTrigger class="w-full" aria-label="Chat memory depth">
					<SelectValue>{{ chatMemoryChosenLabel(chatMemory, uiMode) }}</SelectValue>
				</SelectTrigger>
				<SelectContent>
					<SelectItem v-for="option in chatMemory.depths" :key="option.depth" :value="option.depth">
						{{ chatMemoryDepthLabel(option, chatMemory.depths, uiMode) }}
					</SelectItem>
				</SelectContent>
			</Select>
			<p class="text-muted-foreground text-xs">
				How much of your memory a member reads with each message you send in chat. A deeper read finds notes
				a shallower one misses. It also costs more: recalled notes stay in the conversation and are sent to
				the model on every later step, on your own model keys.
			</p>
		</ZhPanel>
		<ZhPanel
			v-if="unpromptedTurns"
			data-testid="chat-unprompted-turn-limit-section"
			measure="form"
			class="space-y-2"
		>
			<ZhSectionHeading>Unprompted turns</ZhSectionHeading>
			<p v-if="unpromptedTurnsError" class="text-destructive text-sm" role="alert">{{ unpromptedTurnsError }}</p>
			<Select
				:model-value="String(unpromptedTurns.limit)"
				data-testid="chat-unprompted-turn-limit"
				aria-label="Unprompted turns"
				@update:model-value="saveUnpromptedTurns(String($event))"
			>
				<SelectTrigger class="w-full" aria-label="Unprompted turns">
					<SelectValue>{{ chatUnpromptedTurnLimitLabel(unpromptedTurns.limit) }}</SelectValue>
				</SelectTrigger>
				<SelectContent>
					<SelectItem v-for="choice in unpromptedTurnChoices" :key="choice.value" :value="choice.value">
						{{ choice.label }}
					</SelectItem>
				</SelectContent>
			</Select>
			<p class="text-muted-foreground text-xs">
				How many turns in a row a member may take on its own in a chat thread when work it started ends,
				before anyone writes to it. Each of those turns runs on your own model usage, so a lower limit spends
				less. Off means members never carry on by themselves: what the work did still shows in the thread.
				Raising it lets chats that paused at the old limit, or held their results while it was Off, carry on
				straight away for the work that has already ended. The standard is
				{{ chatUnpromptedTurnLimitLabel(CHAT_UNPROMPTED_TURN_LIMITS.defaultLimit) }}.
			</p>
		</ZhPanel>
		<ZhPanel v-if="enterpriseTimezone" data-testid="enterprise-timezone-section" measure="form" class="space-y-2">
			<ZhSectionHeading>Timezone</ZhSectionHeading>
			<p v-if="enterpriseTimezoneError" class="text-destructive text-sm" role="alert">{{ enterpriseTimezoneError }}</p>
			<Select
				:model-value="enterpriseTimezone.timezone"
				data-testid="enterprise-timezone"
				aria-label="Enterprise timezone"
				@update:model-value="saveEnterpriseTimezone(String($event))"
			>
				<SelectTrigger class="w-full" aria-label="Enterprise timezone">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem v-for="zone in timezoneChoices" :key="zone" :value="zone">{{ zone }}</SelectItem>
				</SelectContent>
			</Select>
			<p class="text-muted-foreground text-xs">
				The zone this company runs in, taken from whoever created it. Every schedule set to follow the
				enterprise fires against this, so changing it moves their next run; a schedule that names its own
				zone is left alone.
			</p>
			<template v-if="enterpriseHours">
				<ZhSectionHeading>Working hours</ZhSectionHeading>
				<p v-if="enterpriseHoursError" class="text-destructive text-sm" role="alert">{{ enterpriseHoursError }}</p>
				<p
					v-else-if="enterpriseHoursNotice"
					class="text-muted-foreground text-sm"
					role="status"
					data-testid="enterprise-working-hours-notice"
				>
					{{ enterpriseHoursNotice }}
				</p>
				<form class="flex flex-wrap items-end gap-2" @submit.prevent="saveEnterpriseHours">
					<div class="space-y-1">
						<Label for="enterprise-working-hours-start">Start</Label>
						<Input
							id="enterprise-working-hours-start"
							v-model="enterpriseHoursStart"
							data-testid="enterprise-working-hours-start"
							type="time"
							class="w-32"
						/>
					</div>
					<div class="space-y-1">
						<Label for="enterprise-working-hours-end">End</Label>
						<Input
							id="enterprise-working-hours-end"
							v-model="enterpriseHoursEnd"
							data-testid="enterprise-working-hours-end"
							type="time"
							class="w-32"
						/>
					</div>
					<Button
						type="button"
						size="sm"
						variant="outline"
						:disabled="savingEnterpriseHours"
						data-testid="enterprise-working-hours-save"
						@click="saveEnterpriseHours"
					>
						Save
					</Button>
					<Button
						type="button"
						size="sm"
						variant="ghost"
						:disabled="savingEnterpriseHours || !enterpriseHours?.start"
						data-testid="enterprise-working-hours-clear"
						@click="clearEnterpriseHours"
					>
						Clear
					</Button>
				</form>
				<p class="text-muted-foreground text-xs">
					We send the Needs you email only inside these hours, in this timezone, to people who set none of
					their own. With none set here, the default is 8:00am to 8:00pm.
				</p>
				<p class="text-muted-foreground text-xs" data-testid="enterprise-working-hours-how">
					These hours apply every day. Anything that arrives outside them waits and goes in the next email. An
					end earlier than the start runs past midnight.
				</p>
			</template>
		</ZhPanel>
		<!-- #2217: the enterprise's name. An owner renames it here; anyone else sees it and has no control to change it. -->
		<ZhPanel v-if="me" data-testid="enterprise-name-section" measure="form" class="space-y-2">
			<ZhSectionHeading>Name</ZhSectionHeading>
			<p class="text-sm font-medium" data-testid="enterprise-name-current">{{ enterpriseName }}</p>
			<template v-if="me.isOwner">
				<p v-if="enterpriseNameError" class="text-destructive text-sm" role="alert">{{ enterpriseNameError }}</p>
				<p
					v-else-if="enterpriseNameNotice"
					class="text-muted-foreground text-sm"
					role="status"
					data-testid="enterprise-name-notice"
				>
					{{ enterpriseNameNotice }}
				</p>
				<form data-testid="enterprise-name-form" class="flex items-center gap-2" @submit.prevent="saveEnterpriseName">
					<Input v-model="enterpriseNameInput" data-testid="enterprise-name-input" aria-label="Enterprise name" class="flex-1" />
					<Button
						type="button"
						size="sm"
						variant="outline"
						data-testid="enterprise-name-save"
						:disabled="enterpriseNameBusy"
						@click="saveEnterpriseName"
					>
						{{ enterpriseNameBusy ? 'Saving…' : 'Save' }}
					</Button>
				</form>
				<p class="text-muted-foreground text-xs" data-testid="enterprise-name-help">
					Up to 200 characters. Your company's name in the header, in the list of your enterprises, on the
					map and in search, and in mail and invitations sent from now on. Renaming does not change the
					enterprise's address or its members' email addresses.
				</p>
				<p class="text-muted-foreground text-xs" data-testid="enterprise-name-billing">
					If this enterprise has billing set up, invoices you download from Billing show its current name,
					including ones issued before the rename.
				</p>
			</template>
			<p v-else class="text-muted-foreground text-xs">Only an owner of this enterprise can rename it.</p>
		</ZhPanel>
		<!-- #1118: the one signature template that signs every mailbox without its own. An owner's alone to keep. -->
		<ZhPanel v-if="me?.isOwner === true" data-testid="enterprise-signature-template-section" measure="form">
			<EnterpriseSignatureTemplatePanel />
		</ZhPanel>
		<ZhPanel v-if="changelogRepo" data-testid="enterprise-changelog-repo-section" measure="form" class="space-y-2">
			<ZhSectionHeading>Public changelog</ZhSectionHeading>
			<Label for="enterprise-changelog-repo">Public changelog repository</Label>
			<p v-if="changelogRepoError" id="enterprise-changelog-repo-error" class="text-destructive text-sm" role="alert">
				{{ changelogRepoError }}
			</p>
			<div class="flex items-center gap-2">
				<Input
					id="enterprise-changelog-repo"
					v-model="changelogRepoInput"
					data-testid="enterprise-changelog-repo"
					placeholder="owner/name"
					autocomplete="off"
					maxlength="500"
					:aria-invalid="changelogRepoError ? 'true' : undefined"
					:aria-describedby="
						changelogRepoError
							? 'enterprise-changelog-repo-error enterprise-changelog-repo-help'
							: 'enterprise-changelog-repo-help'
					"
					@keydown.enter.prevent="saveChangelogRepo"
				/>
				<Button
					type="button"
					size="sm"
					variant="outline"
					data-testid="enterprise-changelog-repo-save"
					:disabled="changelogRepoBusy"
					@click="saveChangelogRepo"
				>
					{{ changelogRepoBusy ? 'Saving…' : 'Save' }}
				</Button>
			</div>
			<p
				v-if="changelogRepoSavedNote"
				data-testid="enterprise-changelog-repo-saved"
				class="text-muted-foreground text-sm"
				role="status"
			>
				{{ changelogRepoSavedNote }}
			</p>
			<p id="enterprise-changelog-repo-help" class="text-muted-foreground text-xs">
				Leave empty if you have no public changelog. Otherwise enter the repository that hosts it as owner/name,
				for example acme/site, or as its full address without sign-in details. The name is not checked when you
				save, so check its spelling.
			</p>
		</ZhPanel>
		<!-- #759: one switch for every linked clone. It saves when flipped and goes back with the reason if refused. -->
		<ZhPanel
			v-if="autoUpdateClonedTasks !== null"
			data-testid="enterprise-auto-update-section"
			measure="form"
			class="space-y-2"
			aria-labelledby="enterprise-auto-update-heading"
		>
			<ZhSectionHeading id="enterprise-auto-update-heading">Auto-update cloned tasks</ZhSectionHeading>
			<div class="flex items-start justify-between gap-4 text-sm">
				<div class="space-y-1">
					<Label for="enterprise-auto-update">Apply quiet source updates by themselves</Label>
					<p id="enterprise-auto-update-help" class="text-muted-foreground text-xs">
						Quiet updates to a catalog task apply to your linked clones of it by themselves. Updates that
						add or remove tools or gates, or need a new input value, still wait for a person, whatever this
						says. Turn it off and every update waits for a person.
					</p>
				</div>
				<Switch
					id="enterprise-auto-update"
					:model-value="autoUpdateClonedTasks"
					data-testid="enterprise-auto-update"
					aria-label="Auto-update cloned tasks"
					:aria-describedby="
						autoUpdateClonedTasksError
							? 'enterprise-auto-update-error enterprise-auto-update-help'
							: 'enterprise-auto-update-help'
					"
					:disabled="autoUpdateClonedTasksBusy"
					@update:model-value="saveAutoUpdateClonedTasks"
				/>
			</div>
			<ZhFieldError id="enterprise-auto-update-error" :message="autoUpdateClonedTasksError ?? undefined" />
		</ZhPanel>
		<ZhPanel v-if="concurrency" measure="form" class="space-y-2">
			<ZhSectionHeading>Concurrent runs</ZhSectionHeading>
			<p v-if="concurrencyError" class="text-destructive text-sm">{{ concurrencyError }}</p>
			<form data-testid="enterprise-concurrency-form" class="flex items-center gap-2" @submit.prevent="saveConcurrency">
				<Input v-model="concurrencyInput" data-testid="enterprise-concurrency" type="number" min="1" class="w-24" />
				<Button type="submit" size="sm" variant="outline">Save</Button>
			</form>
			<p class="text-muted-foreground text-xs">
				How many tasks the whole enterprise can run at once, across every team. Raising it lets different
				members run in parallel; one active run per member still applies regardless of this limit.
			</p>
		</ZhPanel>
		<ul v-if="gates.length" class="space-y-2">
			<ZhPanel v-for="gate in gates" :key="gate.id" as="li" class="text-sm">
				<RouterLink :to="`/runs/${gate.runId}`">{{ gate.toolName }}</RouterLink>
			</ZhPanel>
		</ul>
		<FirstRunHint
			v-else-if="firstRun"
			lead="Nothing is waiting, running, or blocked yet. Your team's runs and results appear here."
			:action="{ label: 'Talk to the COO', to: '/chat' }"
		/>
		<p v-else class="text-muted-foreground text-sm">
			Nothing is waiting on you. Company-wide MCP tools and agent keys bind below; an agent’s own
			tools bind on that member.
		</p>
		<section v-if="me" class="space-y-3">
			<ZhSectionHeading>Agents</ZhSectionHeading>
			<AgentConnectionsPanel layer="enterprise" :layer-id="me.enterprise.id" />
		</section>
		<section v-if="me" class="space-y-3">
			<ZhSectionHeading>Tools</ZhSectionHeading>
			<ToolBindings lock-layer="enterprise" :lock-layer-id="me.enterprise.id" />
		</section>
		<EnterpriseDangerZone v-if="me?.isOwner === true" :enterprise-id="me.enterprise.id" :name="me.enterprise.name" />
	</section>
</template>
